import {
  type FundCommandSelection,
  isFixedFractionVFrame,
  isFixedStringVFrame,
  type PartitionCommandSelection,
  type PartitionCommandSelectionMFrame,
  type SwitchSubPortfolioGroupCommandData,
} from '@festina/unitlink-general-types/unitlink'
import { useFetchFunds } from '@festina-life/api'
import {
  amountUtils,
  type Compare,
  listUtils,
  notEmpty,
  type Optional,
  stringUtils,
} from '@festina-life/core'
import { Button } from '@festina-life/ui/actions'
import { useFormikContext, useTranslate } from '@festina-life/ui/hooks'
import {
  createColumnDefiner,
  type RowAction,
  SortableTable,
} from '@festina-life/ui/listsAndTables'
import { CompareValues, Typography } from '@festina-life/ui/structure'
import unionWith from 'lodash-es/unionWith'
import { useMemo, useState } from 'react'

import { useHook } from '../../../lenteHooks'
import { EditFundCommandSelectionModal } from './EditFundCommandSelectionModal'
import {
  updateFundCommandSelections,
  updatePartitionCommandSelections,
} from './switchSubPortfolioGroupUtils'
import type { FundCommandSelectionWithCurrentFractions } from './types'

type Props = {
  mFrame: Optional<PartitionCommandSelectionMFrame>
  partitionCommandSelection: PartitionCommandSelection
  title: string
}

const defineColumn =
  createColumnDefiner<Compare<FundCommandSelectionWithCurrentFractions>>()

export const FundCommandSelectionTable = ({
  mFrame,
  partitionCommandSelection,
  title,
}: Props) => {
  const tenantId = useHook('routing.tenant.useTenantParam')()
  const translate = useTranslate()
  const {
    values,
    setFieldValue,
    status: { shouldFieldsBeDisabled },
  } = useFormikContext<SwitchSubPortfolioGroupCommandData>()

  const { data: funds } = useFetchFunds({ tenantId })

  const [editFundCommandSelection, setEditFundCommandSelection] =
    useState<FundCommandSelection>()

  const onEditFundClose = () => setEditFundCommandSelection(undefined)

  const onEditFundSubmit = async (updatedFund: FundCommandSelection) => {
    if (!partitionCommandSelection) {
      return
    }

    const updatedFundSelections = updateFundCommandSelections(
      updatedFund,
      partitionCommandSelection.fundCommandSelections?.filter(notEmpty) ?? [],
    )

    const updatedPartitionCommandSelections = updatePartitionCommandSelections(
      {
        ...partitionCommandSelection,
        _type: 'PartitionCommandSelection',
        fundCommandSelections: updatedFundSelections,
      },
      values?.partitionCommandSelections?.filter(notEmpty) ?? [],
    )

    await setFieldValue(
      'partitionCommandSelections',
      updatedPartitionCommandSelections,
    )
    onEditFundClose()
  }

  const onDeleteFundCommandSelection = async (
    fund: FundCommandSelectionWithCurrentFractions,
  ) => {
    const existingFunds = partitionCommandSelection?.fundCommandSelections ?? []
    const updatedSelections = existingFunds.filter(
      (selection) => selection?.isinKey !== fund.isinKey,
    )

    const updatedPartition: PartitionCommandSelection = {
      ...partitionCommandSelection,
      _type: 'PartitionCommandSelection',
      fundCommandSelections: updatedSelections,
    }

    await setFieldValue(
      'partitionCommandSelections',
      updatePartitionCommandSelections(
        updatedPartition,
        values?.partitionCommandSelections?.filter(notEmpty) ?? [],
      ),
    )
  }

  const existingFundMFrames =
    mFrame?.updateFundCommandSelections?.filter(notEmpty) ?? []

  const existingFundCommandSelections: FundCommandSelectionWithCurrentFractions[] =
    existingFundMFrames?.map((fund) => ({
      _type: 'FundCommandSelection',
      isinKey: isFixedStringVFrame(fund.isinKey)
        ? fund.isinKey.value
        : undefined,
      currentFraction: fund.currentFraction,
      currentCashValueFraction: fund.currentCashValueFraction,
    })) ?? []

  const updatedFundCommandSelections: FundCommandSelectionWithCurrentFractions[] =
    values.partitionCommandSelections
      ?.find(
        (partition) =>
          partition?.profileType === partitionCommandSelection.profileType,
      )
      ?.fundCommandSelections?.filter(notEmpty)
      .map((updatedFund) => {
        const existingFund = existingFundCommandSelections.find(
          (existing) => existing.isinKey === updatedFund.isinKey,
        )

        const currentFraction = stringUtils.strictIncludes(
          values?.switchCommandType,
          ['paymentAndSavingsDistribution', 'paymentDistribution'],
        )
          ? updatedFund.fraction
          : existingFund?.currentFraction

        const currentCashValueFraction = stringUtils.strictIncludes(
          values?.switchCommandType,
          ['paymentAndSavingsDistribution', 'savingsDistribution'],
        )
          ? updatedFund.fraction
          : existingFund?.currentCashValueFraction

        return {
          _type: 'FundCommandSelection',
          isinKey: updatedFund.isinKey,
          currentFraction,
          currentCashValueFraction,
        }
      }) ?? []

  const canDelete = (fund: FundCommandSelectionWithCurrentFractions) => {
    const isExisting = existingFundCommandSelections.find(
      (existing) => existing.isinKey === fund.isinKey,
    )
    return !shouldFieldsBeDisabled && !isExisting
  }

  const canEdit = (fund: FundCommandSelectionWithCurrentFractions) => {
    const fundFrame = existingFundMFrames.find((mFrame) => {
      const isinKeyFrame = mFrame?.isinKey
      return (
        isFixedStringVFrame(isinKeyFrame) && isinKeyFrame.value === fund.isinKey
      )
    })
    return (
      !shouldFieldsBeDisabled && !isFixedFractionVFrame(fundFrame?.fraction)
    )
  }

  const currentFunds = unionWith(
    updatedFundCommandSelections,
    existingFundCommandSelections,
    (a, b) => a.isinKey === b.isinKey,
  )

  const fundCommandSelections = listUtils.getListOfPairs({
    currentList: currentFunds,
    prevList: existingFundCommandSelections,
    keyFunc: ({ isinKey }) => isinKey,
  })

  const totalRow: Compare<FundCommandSelectionWithCurrentFractions> = useMemo(
    () =>
      fundCommandSelections.reduce<
        Compare<FundCommandSelectionWithCurrentFractions>
      >(
        (acc, curr) => ({
          current: {
            _type: 'FundCommandSelection',
            isinKey: translate('total'),
            currentFraction: amountUtils.addMaybe(
              acc.current?.currentFraction,
              curr.current?.currentFraction,
            ),
            currentCashValueFraction: amountUtils.addMaybe(
              acc.current?.currentCashValueFraction,
              curr.current?.currentCashValueFraction,
            ),
          },
          compare: {
            _type: 'FundCommandSelection',
            isinKey: translate('total'),
            currentFraction: amountUtils.addMaybe(
              acc.compare?.currentFraction,
              curr.compare?.currentFraction,
            ),
            currentCashValueFraction: amountUtils.addMaybe(
              acc.compare?.currentCashValueFraction,
              curr.compare?.currentCashValueFraction,
            ),
          },
        }),
        {
          current: { _type: 'FundCommandSelection' },
          compare: { _type: 'FundCommandSelection' },
        },
      ),
    [translate, fundCommandSelections],
  )

  // For some reason the funds available for adding are given as a list of frames with multiple entries each, so we have to flatten it
  const allAvailableIsinKeys = [
    ...new Set(
      mFrame?.addFundCommandSelections
        ?.map((frame) =>
          isFixedStringVFrame(frame?.isinKey) ? frame.isinKey.value : undefined,
        )
        .filter(notEmpty) ?? [],
    ),
  ]

  // Remove already added partitions so you can't add them again
  const remainingAvailableFunds = allAvailableIsinKeys.filter(
    (isinId) => !currentFunds.find((selection) => selection.isinKey === isinId),
  )

  const existingIsinKeys =
    currentFunds.map((value) => value.isinKey).filter(notEmpty) ?? []

  const rowActions = ({
    current,
    compare,
  }: Compare<FundCommandSelectionWithCurrentFractions>): RowAction[] => {
    const fund = current ?? compare
    return [
      {
        key: 'edit',
        title: translate('edit'),
        iconName: 'Pencil',
        onClick: canEdit(fund)
          ? () => setEditFundCommandSelection(fund)
          : undefined,
      },
      {
        key: 'delete',
        title: translate('delete'),
        iconName: 'Trash',
        onClick: canDelete(fund)
          ? () => onDeleteFundCommandSelection(fund)
          : undefined,
      },
    ]
  }

  const cols = useMemo(() => {
    const allFunds = funds?.fundItems?.filter(notEmpty) ?? []
    return {
      fund: defineColumn({
        heading: translate('fundCommandSelectionTable.fund'),
        keyPath: undefined,
        formatter: ({ compare, current }) => (
          <CompareValues
            keyPath="isinKey"
            prev={compare}
            current={current}
            formatter={(isinKey) =>
              allFunds.find((fund) => fund.isinKey === isinKey)?.name || isinKey
            }
            isDiffing
          />
        ),
        sortValue: (x) =>
          allFunds.find((fund) => fund.isinKey === x.current?.isinKey)?.name ||
          x.current?.isinKey,
      }),
      currentFraction: defineColumn({
        heading: translate('fundCommandSelectionTable.currentFraction'),
        keyPath: undefined,
        formatter: ({ compare, current }) => (
          <CompareValues
            keyPath="currentFraction"
            prev={compare}
            current={current}
            formatter={amountUtils.formatPercent}
            isDiffing
          />
        ),
        sortValue: (x) => x.current?.currentFraction,
      }),
      currentCashValueFraction: defineColumn({
        heading: translate(
          'fundCommandSelectionTable.currentCashValueFraction',
        ),
        keyPath: undefined,
        formatter: ({ compare, current }) => (
          <CompareValues
            keyPath="currentCashValueFraction"
            prev={compare}
            current={current}
            formatter={amountUtils.formatPercent}
            isDiffing
          />
        ),
        sortValue: (x) => x.current?.currentCashValueFraction,
      }),
    }
  }, [funds?.fundItems, translate])

  return (
    <>
      <div className="flex items-end mb-2 mt-4">
        <Typography intensity="primary" weight="bold" className="grow">
          {title}
        </Typography>
        <Button
          className="self-end"
          variant="secondary"
          disabled={!remainingAvailableFunds.length}
          onClick={() =>
            setEditFundCommandSelection({ _type: 'FundCommandSelection' })
          }
        >
          {translate('fundCommandSelectionTable.addFund')}
        </Button>
      </div>
      <SortableTable
        data={fundCommandSelections}
        footerData={[totalRow]}
        columns={cols}
        rowActions={rowActions}
      />
      {editFundCommandSelection && (
        <EditFundCommandSelectionModal
          fundCommandSelection={editFundCommandSelection}
          existingFundIsinKeys={existingIsinKeys}
          onClose={onEditFundClose}
          onSubmit={onEditFundSubmit}
          mFrame={mFrame}
        />
      )}
    </>
  )
}
